import React, {useRef, useState} from "react";
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";

import useChangeOnePartWordMetadata from "./hooks/useChangeOnePartWordMetadata";
import getSubPositionIndex from "./functions/getSubPositionIndex";
import usePhraseTypeIndexes from "../../hooks/usePhraseTypeIndexes";
import ChangeOnePartTutor from "./ChangeOnePartTutor";
import ChangeOnePartAssessment from "./ChangeOnePartAssessment";

import { READING_MODES, phraseTypes } from "../../../../../constants/constants";
import { selectReaderManager } from "../../../ReaderManagerEvents/ReaderManagerSlice";

/**
 * A wrapper that exports a component either 'ChangeOnePartAssessment' or
 * 'ChangeOnePartTutor' based on the current reading mode.
 *
 * @visibleName ChangeOnePartWrapper
 *
 * @param {Object} props The props object that contains properties for the component
 *
 * @param {Number} props.currentPageIndex The current page number
 * @param {String} props.originalWord The original word that needs to be changed
 * @param {String} props.targetWord The target word to which original word should be changed
 * @param {Object} props.phraseMetaData The phrase meta data object
 * @param {Object} props.speech The speech object
 * @param {Boolean} props.paused A flag that indicates if component is paused
 * @param {Function} props.setWillHandlePause A function that sets the flag that indicates if component has to be paused
 * @param {Function} props.startPhrase A function that starts the current phrase
 * @param {Function} props.pausePhrase A function that pauses the current phrase
 * @param {Function} props.resumePhrase A function that resumes the paused phrase
 * @param {Function} props.doneHandlingPause A function that is called when we are done handling pause
 * @param {Object} props.transcript The transcript object
 * @param {Boolean} props.isInstruction A flag that indicates if the phrase is an instruction
 * @param {Boolean} props.hasInstructions A flag that indicates if the phrase has instructions
 * @param {Boolean} props.isFTUE A flag that indicates if the phrase is an FTUE
 * @param {Number} props.currentWordIndex The current word index
 *
 * @returns {React.Element}
 */
export default function ChangeOnePartWrapper({
    currentPageIndex,
    originalWord,
    targetWord,
    phraseMetaData,
    speech,
    paused = false,
    setWillHandlePause = () => {},
    startPhrase = () => {},
    pausePhrase = () => {},
    resumePhrase = () => {},
    doneHandlingPause = () => {},
    transcript,
    isInstruction,
    hasInstructions,
    isFTUE,
    currentWordIndex,
    setDisableInterventionsForPhrase,
    setAllowFirstWordSkipOnPause = () => {},
}) {
    const [ftueNotSeen, setFtueNotSeen] = useState(true);
    const { contextStates } = useSelector(selectReaderManager)
    const { readingMode } = contextStates
    const exampleWords = isFTUE && ftueNotSeen && phraseMetaData.instructions && phraseMetaData.instructions.example ? true : false;

    const targetWordToDisplay = exampleWords ? phraseMetaData.instructions.example.targetWord : targetWord;
    const originalWordToDisplay = exampleWords ? phraseMetaData.instructions.example.originalWord : originalWord;

    const { wordMetadata, subWordMetadata, exampleWordMetaData, exampleSubWordMetaData, error, loading } = useChangeOnePartWordMetadata({
            phraseMetaData,
            targetWord,
            originalWord,
        });

    const {
        isExamplePhraseOfThisType,
        isLastPhraseOfThisType,
        currentPhraseIndexOfThisType,
        isLastPageOfStory,
    } = usePhraseTypeIndexes(currentPageIndex, phraseTypes.CHANGE_ONE_PART);

    /**
     * error in loading word metadata
     */
    if (error) return <div>Unable to load some data. Please try again.</div>

    /**
     * data not ready, just return
     */

    if (!phraseMetaData || !originalWordToDisplay || !targetWordToDisplay) return null // loading prop data
    if (loading) return null // fetching word metadata

    const wordMetaDataToDisplay = exampleWords ? exampleWordMetaData : wordMetadata;
    const subWordMetaDataToDisplay = exampleWords ? exampleSubWordMetaData : subWordMetadata;

    // calculate the difference between the word and the text as start or end
    const subPositionIndex = getSubPositionIndex({ targetWordToDisplay, originalWordToDisplay })
    const subPosition = subPositionIndex === 0 ? "start" : "end";

    /**
     * build the metadata that is shared between assessment and tutor
     * any difference in the metadata is handled in the component itself
     */
    const metadata = {
        version: phraseMetaData.version || 1, // default to 1 (orthographic)
        speech: speech, // the speech instance for both speak and listen
        word: originalWordToDisplay,
        wordMetadata: {
            PHON: wordMetaDataToDisplay.PHON, // assessment mode does not sound out the phonemes but requires it to be truthy to load
            GRAPHEMIC_BREAKDOWN: wordMetaDataToDisplay.GRAPHEMIC_BREAKDOWN,
            SUB_POSITION: subPosition,
            SUBS: [
                {
                    word: targetWordToDisplay,
                    phon: subWordMetaDataToDisplay.PHON,
                    phraseIndex: currentPhraseIndexOfThisType,
                    isLastPhrase: isLastPhraseOfThisType,
                    isExample: isExamplePhraseOfThisType,
                },
            ],
        },
        assessmentMode: readingMode === READING_MODES.ASSESSMENT,
        tutorMode: readingMode === READING_MODES.TUTOR,
        endOfPhrase: () => null, // do not implement, only intervention uses it
        phraseMetaData: phraseMetaData,
        updateFtueSeen: () => {setFtueNotSeen(false)},
        ftueNotSeen: ftueNotSeen,
    }

    /**
     * common props for both assessment and tutor
     */
    const commonProps = {
        metadata,
        paused,
        setWillHandlePause,
        startPhrase,
        pausePhrase,
        resumePhrase,
        doneHandlingPause,
    }

    /**
     * assessment mode
     */
    if (readingMode === READING_MODES.ASSESSMENT) {
        return (
            <ChangeOnePartAssessment
                {...commonProps}
                loading={loading}
                transcript={transcript}
                currentPageIndex={currentPageIndex}
            />
        );
    }

    /**
     * tutor mode
     */
    return (
        <ChangeOnePartTutor
            {...commonProps}
            transcript={transcript}
            currentWordIndex={currentWordIndex}
            isInstruction={isInstruction}
            hasInstructions={hasInstructions}
            isFTUE={isFTUE}
            setDisableInterventionsForPhrase={setDisableInterventionsForPhrase}
            setAllowFirstWordSkipOnPause={setAllowFirstWordSkipOnPause}
        />
    );
}

// prop types
ChangeOnePartWrapper.propTypes = {
    currentPageIndex: PropTypes.number.isRequired,
    originalWord: PropTypes.string.isRequired,
    targetWord: PropTypes.string.isRequired,
    phraseMetaData: PropTypes.object.isRequired,
    speech: PropTypes.object.isRequired,
    paused: PropTypes.bool.isRequired,
    setWillHandlePause: PropTypes.func.isRequired,
    startPhrase: PropTypes.func.isRequired,
    pausePhrase: PropTypes.func.isRequired,
    resumePhrase: PropTypes.func.isRequired,
    doneHandlingPause: PropTypes.func.isRequired,
    transcript: PropTypes.object.isRequired,
    isInstruction: PropTypes.bool.isRequired,
    hasInstructions: PropTypes.bool.isRequired,
    isFTUE: PropTypes.bool.isRequired,
    currentWordIndex: PropTypes.number.isRequired,
    setDisableInterventionsForPhrase: PropTypes.func.isRequired,
    setAllowFirstWordSkipOnPause: PropTypes.func,
}